import {
  Flex,
  Text as ChakraText,
  Tooltip,
  Box,
  IconButton,
  Textarea,
  HStack,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { FileDetailsResponse } from '../../../interfaces/FileDetailsResponse'
import useToast from '../../../hooks/useToast'
import { EditIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons'
import { VscSparkle } from 'react-icons/vsc'
import { useFetch, CachePolicies } from 'use-http'

const FileDescription = ({
  fileDetails,
}: {
  fileDetails: FileDetailsResponse
}) => {
  const isDescriptionEditable = fileDetails.type === 'file'
  const isImage = fileDetails.fileType === 'image'

  const { request, loading, response, error } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const { t } = useTranslation()
  const toast = useToast()

  const [isEditing, setIsEditing] = useState(false)
  const [description, setDescription] = useState(fileDetails.description || '')
  const [tempDescription, setTempDescription] = useState(description)
  const [isGenerating, setIsGenerating] = useState(false)

  const handleApprove = async () => {
    const data = await request.patch(
      `/api/v2/files/${fileDetails.fileId}/details`,
      {
        description: tempDescription.trim(),
      },
    )
    if (response.ok) {
      setDescription(data.description || '')
      toast({
        description: t(
          'update_description_success',
          'Description updated successfully.',
        ),
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
        variant: 'outline',
      })
    } else {
      toast({
        title: t('update_description_error', 'Description update failed.'),
        description:
          data.message ||
          data.description ||
          error?.message ||
          t('unknown_error', 'An unknown error occurred'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
        variant: 'outline',
      })
    }
    setIsEditing(false)
  }

  const handleCancel = () => {
    setTempDescription(description)
    setIsEditing(false)
  }

  const handleAutoDescription = async () => {
    setIsGenerating(true)

    const data = await request.patch(
      `/api/v2/files/${fileDetails.fileId}/details`,
      {
        extensions: [{ name: 'ai-auto-description' }],
      },
    )

    if (response.ok) {
      switch (data.extensionStatus?.['ai-auto-description']) {
        case 'pending': {
          setIsGenerating(false)
          setIsEditing(false)
          toast({
            title: 'Description will update shortly.',
            description: 'AI is generating your description.',
            status: 'info',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
            variant: 'outline',
          })
          break
        }
        case 'failed': {
          setIsGenerating(false)
          setIsEditing(false)
          toast({
            description: 'Failed to generate a description.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
            variant: 'outline',
          })
          break
        }
        default: {
          setDescription(data.description || '')
          setTempDescription(data.description || '')
          setIsEditing(false)
          setIsGenerating(false)
          toast({
            description: 'Successfully applied auto description.',
            status: 'success',
            duration: 5000,
            isClosable: true,
            position: 'top-right',
            variant: 'outline',
          })
        }
      }
    } else {
      toast({
        title: t('update_description_error', 'Description update failed.'),
        description:
          data.message ||
          data.description ||
          error?.message ||
          t('unknown_error', 'An unknown error occurred'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
        variant: 'outline',
      })
      return
    }
  }

  return (
    <Flex marginBottom="10px" direction="column">
      {!isEditing ? (
        <Box>
          <Flex justify="space-between" align="center">
            <ChakraText>{t('Description', 'Description')} :</ChakraText>
            <Tooltip label={t('Edit', 'Edit')} hasArrow>
              <IconButton
                icon={<EditIcon />}
                size="sm"
                variant="ghost"
                aria-label={t('Edit Description', 'Edit Description')}
                onClick={() => setIsEditing(true)}
                isDisabled={!isDescriptionEditable}
              />
            </Tooltip>
          </Flex>
          <ChakraText as="b" className="description">{description}</ChakraText>
        </Box>
      ) : (
        <>
          <Box position="relative" mt={1}>
            <Textarea
              value={tempDescription}
              onChange={e => setTempDescription(e.target.value)}
              pr="2rem"
              size="sm"
              fontSize="sm"
              resize="none"
            />
            <Tooltip
              label={t(
                'generate_description_for_image',
                'Generate description for the image',
              )}
              hasArrow
            >
              <IconButton
                icon={<VscSparkle />}
                isLoading={isGenerating}
                isDisabled={!isImage || loading}
                spinnerPlacement="start"
                size="sm"
                variant="ghost"
                position="absolute"
                top="0.3rem"
                right="0.3rem"
                aria-label={t(
                  'generate_description_for_image',
                  'Generate description for the image',
                )}
                onClick={handleAutoDescription}
                sx={{
                  '&:hover svg': {
                    animation: 'sparkle 0.6s ease-in-out',
                  },
                }}
              />
            </Tooltip>
          </Box>
          <HStack mt={1} justify="flex-end" spacing={3}>
            <IconButton
              icon={<CheckIcon />}
              size="sm"
              borderRadius="md"
              aria-label="Save"
              onClick={handleApprove}
              isDisabled={isGenerating || loading}
            />
            <IconButton
              icon={<CloseIcon />}
              variant="outline"
              size="sm"
              borderRadius="md"
              aria-label="Cancel"
              onClick={handleCancel}
              isDisabled={isGenerating || loading}
            />
          </HStack>
        </>
      )}
    </Flex>
  )
}

export default FileDescription
